import React, { useState } from "react";
import BrowserHistoryDispatcher from "dispatchers/browser-history-dispatcher";
import { isEssentialOrActiveTrial } from "util/brand-stripe-belt-helper";
import { cn } from "util/cn";
import { H6 } from "visual-components/util/texts";

/**
 * BonusOptionItem component for displaying a single bonus option
 * @param {object} props - Component properties
 * @param {object} props.item - The bonus option item
 * @param {string} props.item.key - The unique key for the bonus option
 * @param {string} props.item.label - The label for the bonus option
 * @param {string} props.item.colour - The background colour for the bonus option
 * @param {string} [props.item.icon] - The FontAwesome icon class for the bonus option
 * @param {import("react").ReactElement} [props.item.iconComponent] - The React component for the icon
 * @param {boolean} [props.item.textWhite] - Whether to use white text
 * @param {function} props.addBonusItem - Function to call when the bonus option is clicked
 * @param {object} props.currentBrand - The current brand object
 * @returns {import("react").ReactElement} - The rendered component
 */
export default function BonusOptionItem(props) {
  const {
    item: { key, label, colour, icon, iconComponent, textWhite },
    addBonusItem,
    currentBrand,
  } = props;

  const [showTooltip, setShowTooltip] = useState(false);

  // Check if this is appDownload and user is on Essential or Trial plan
  const isAppDownload = key === "appDownload";
  const isEssentialOrTrial = isEssentialOrActiveTrial(currentBrand);
  const isLocked = isAppDownload && isEssentialOrTrial;

  // Determine if text should be white based on background color
  const useWhiteText = textWhite ?? key !== "snapchatFollow";

  const handleClick = () => {
    if (isLocked) {
      // Navigate to upgrade page
      BrowserHistoryDispatcher.push("/upgrade-plan");
    } else {
      addBonusItem(key);
    }
  };

  const handleMouseEnter = () => {
    if (isLocked) {
      setShowTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    if (isLocked) {
      setShowTooltip(false);
    }
  };

  return (
    <div className="relative" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <div
        onClick={handleClick}
        className={cn(
          "w-[230px] h-14 rounded-xl mb-4 px-5 py-3.5",
          "flex items-center justify-between",
          "transition-all duration-300 ease-in-out",
          "translate-y-0 shadow-[0_3px_10px_0_rgba(0,0,0,0.15)]",
          "focus:outline-none focus:ring-2 focus:ring-offset-2",
          useWhiteText ? "focus:ring-white/40" : "focus:ring-black/20",
          isLocked
            ? "cursor-pointer opacity-60"
            : "cursor-pointer hover:-translate-y-0.5 hover:shadow-[0_6px_20px_0_rgba(0,0,0,0.25)]",
        )}
        style={{
          background: colour,
          backgroundColor: colour,
        }}
      >
        <div className="flex items-center gap-3">
          {icon && (
            <i
              className={cn(
                "fa",
                icon,
                "text-xl w-6 text-center",
                useWhiteText ? "text-white" : "text-[#2c3541]",
              )}
              aria-hidden="true"
            />
          )}
          {iconComponent && <>{iconComponent}</>}
          <H6 white={useWhiteText} className="m-0 font-semibold text-sm tracking-[-0.01em]">
            {label}
          </H6>
        </div>
        <div
          className={cn(
            "w-7 h-7 rounded-full flex items-center justify-center",
            "transition-colors duration-300",
            useWhiteText ? "bg-white/20" : "bg-black/10",
          )}
        >
          {isLocked ? (
            <i
              className={cn("fa fa-lock text-sm", useWhiteText && "text-white")}
              aria-hidden="true"
            />
          ) : (
            <i
              className={cn("fa fa-plus text-sm", useWhiteText && "text-white")}
              aria-hidden="true"
            />
          )}
        </div>
      </div>

      {/* Tooltip */}
      {isLocked && showTooltip && <UpgradeTooltip />}
    </div>
  );
}

/**
 * Tooltip component for upgrade prompt
 * @returns {import("react").ReactElement} - The rendered tooltip component
 */
function UpgradeTooltip() {
  return (
    <div
      className={cn(
        "absolute left-0 right-0 -bottom-12 z-50",
        "bg-[#2c3541] text-white rounded-lg mt-6 py-2 px-3",
        "shadow-lg text-center",
        "animate-in fade-in duration-200",
        "min-h-14",
      )}
    >
      Requires{" "}
      <span
        className="underline cursor-pointer font-semibold"
        onClick={(e) => {
          e.stopPropagation();
          BrowserHistoryDispatcher.push("/upgrade-plan");
        }}
      >
        Upgrade
      </span>
      <div
        className="absolute left-1/2 -translate-x-1/2 -top-1"
        style={{
          width: 0,
          height: 0,
          borderLeft: "6px solid transparent",
          borderRight: "6px solid transparent",
          borderBottom: "6px solid #2c3541",
        }}
      />
    </div>
  );
}
